import { useCallback, useState } from 'react'

interface AsyncHookParams<T, R> {
	call(payload: T): Promise<R>

	onError?<E extends Error>(errText: string, err: E): void

	errorTextSerializer?<E extends Error>(err: E): string
}

interface UserAsyncHookResult<T, R> {
	data: null | R
	errorText: null | string

	runRequest(payload?: T): Promise<R>

	clearError(): void

	isLoading: boolean
}

export const createAsyncHook = <T, R>(params: AsyncHookParams<T, R>): (() => any) => {
	return function useAsyncHookWrapper() {
		const [data, setData] = useState<R | null>(null)
		const [errText, setErrText] = useState<string | null>(null)
		const [isLoading, setLoadingStatus] = useState(false)

		const handleRequest = useCallback(
			async (callParams) => {
				try {
					setLoadingStatus(true)
					const result = await params.call(callParams)
					setData(result)
					return result
				} catch (err: any) {
					const errText = params.errorTextSerializer ? params.errorTextSerializer(err) : err.message
					setData(null)
					setErrText(errText)
					params.onError && params.onError(errText, err)
					return null
				} finally {
					setLoadingStatus(false)
				}
			},
			[params]
		)

		const handleClearError = useCallback(() => {
			setErrText(null)
		}, [])

		return {
			data,
			errorText: errText,
			runRequest: handleRequest,
			clearError: handleClearError,
			isLoading,
		}
	}
}
